import { ReactNode } from 'react';
import clsx from 'clsx';

export type ApprovalWorkflow = 'basic' | 'advanced';

type Props = {
  value: ApprovalWorkflow;
  onChange: (workflow: ApprovalWorkflow) => void;
  children?: ReactNode;
};

const WORKFLOWS: {
  value: ApprovalWorkflow;
  title: string;
  description: string;
  badge?: string;
}[] = [
  {
    value: 'basic',
    title: 'Basic',
    badge: 'Default',
    description:
      'A failed check holds the inspection at Criteria not met. The inspection creator can follow up with the seller to fix any issues, or raise a single exception request that moves it to Submitted.'
  },
  {
    value: 'advanced',
    title: 'Advanced approval',
    description:
      'A failed check holds the inspection at Criteria not met. The inspection creator can follow up with the seller to fix any issues, but must comment on each blocked check to move it to In Review for a nominated approver.'
  }
];

const RadioMark = ({ selected }: { selected: boolean }) => (
  <span
    aria-hidden="true"
    className={clsx(
      'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border',
      selected ? 'border-secondary' : 'border-grey-300'
    )}>
    {selected && <span className="h-2 w-2 rounded-full bg-secondary" />}
  </span>
);

const WorkflowChoice = ({ value, onChange, children }: Props) => (
  <div
    role="region"
    aria-labelledby="choose-workflow-heading"
    className="rounded-xl border border-grey-200 bg-white p-5">
    <h3
      id="choose-workflow-heading"
      className="text-sm font-semibold text-grey-900">
      Choose a workflow
    </h3>
    <p className="mt-1 text-sm text-grey-500">
      Advanced adds lender approval sign-off.
    </p>
    <div
      role="radiogroup"
      aria-label="Choose a workflow"
      className="mt-4 grid gap-3 md:grid-cols-2">
      {WORKFLOWS.map((workflow) => {
        const selected = value === workflow.value;

        return (
          <button
            key={workflow.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(workflow.value)}
            className={clsx(
              'rounded-lg border p-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2',
              selected
                ? 'border-secondary bg-grey-50'
                : 'border-grey-200 bg-white hover:border-grey-300'
            )}>
            <div className="flex items-center gap-2">
              <RadioMark selected={selected} />
              <p className="text-sm font-semibold text-grey-900">
                {workflow.title}
              </p>
              {workflow.badge && (
                <span className="text-[11px] font-medium uppercase tracking-wide text-grey-500">
                  {workflow.badge}
                </span>
              )}
            </div>
            <p className="mt-2 text-sm leading-5 text-grey-600">
              {workflow.description}
            </p>
          </button>
        );
      })}
    </div>
    {children && (
      <div className="mt-5 border-t border-grey-200 pt-5">{children}</div>
    )}
  </div>
);

export default WorkflowChoice;
